// src/context/PostContext.js
import { createContext, useContext, useEffect, useState } from 'react';
import api from '../api';
import { POST_VIEW_COUNT_UPDATED_EVENT } from '../services/postViewTracker';

const PostContext = createContext();

export function PostProvider({ children }) {
  const [posts, setPosts] = useState([]);
  const [postInteractions, setPostInteractions] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasLoadedPosts, setHasLoadedPosts] = useState(false);

  // current feed mode ('all' | 'following' | 'merchants' | 'verified')
  const [feedMode, setFeedMode] = useState('all');

  // preserve the seed for the current feed session so pagination stays consistent
  const [feedSeed, setFeedSeed] = useState(null);

  // --- normalize one post so legacy top-level reads stay valid ---
  const normalizePost = (post) => {
    // leave sponsored items untouched
    if (post?.type === 'sponsored') return post;

    const baseUser = post.user || {};

    const resolvedIsAdmin =
      baseUser.isAdmin === true ||
      post.isAdmin === true ||
      !!baseUser.adminRole ||
      !!post.adminRole;

    const resolvedAdminRole =
      baseUser.adminRole ??
      post.adminRole ??
      null;

    const resolvedAccountType =
      baseUser.accountType ||
      post.accountType ||
      'primary';

    const resolvedIsApp =
      baseUser.isApp === true ||
      post.isApp === true ||
      resolvedAccountType === 'app';

    const userSnap = {
      name: baseUser.name ?? post.name,
      username: baseUser.username ?? post.username,
      profilePic: baseUser.profilePic ?? post.profilePic ?? '',
      userType: baseUser.userType ?? post.userType ?? 'pioneer',
      isMerchant: baseUser.isMerchant ?? post.isMerchant ?? false,
      merchantActive: baseUser.merchantActive ?? post.merchantActive ?? false,
      isAdmin: resolvedIsAdmin,
      adminRole: resolvedAdminRole,
      accountType: resolvedAccountType,
      isApp: resolvedIsApp,

      location: baseUser.location ?? post.location ?? '',

      badges: Array.isArray(baseUser.badges) ? baseUser.badges : [],
      badgesBrief: Array.isArray(baseUser.badgesBrief) ? baseUser.badgesBrief : [],
    };

    const enrichedPost = {
      ...post,
      user: userSnap,
      // mirror critical fields back to top-level if missing
      name: post.name ?? userSnap.name,
      username: post.username ?? userSnap.username,
      profilePic: post.profilePic ?? userSnap.profilePic,
      userType: post.userType ?? userSnap.userType,
      isMerchant: post.isMerchant ?? userSnap.isMerchant,
      merchantActive: post.merchantActive ?? userSnap.merchantActive,
      isAdmin: post.isAdmin ?? userSnap.isAdmin,
      adminRole: post.adminRole ?? userSnap.adminRole,
      accountType: post.accountType ?? userSnap.accountType,
      isApp: post.isApp ?? userSnap.isApp,

      location: post.location ?? userSnap.location,
      viewCount: Math.max(0, Number(post.viewCount) || 0),
    };

    // Normalize mentions using lowercase @username keys
    const mentionMap = {};
    (post.mentions || []).forEach((m) => {
      const uname = (m?.username || '').toLowerCase();
      if (uname) {
        const key = `@${uname}`;
        mentionMap[key] = { username: m.username, type: m.type };
      }
    });
    enrichedPost.mentionMap = mentionMap;

    return enrichedPost;
  };

  const enrichPosts = (data) => data.map(normalizePost);

  const addPost = (newPost) => {
    setPosts((prev) => [normalizePost(newPost), ...prev]);
  };

  // Update/replace a post in the feed state
  const updatePostInState = (updatedPost) => {
    if (!updatedPost?._id) return;

    const normalized = normalizePost(updatedPost);

    setPosts((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;

      let found = false;
      const next = prev.map((p) => {
        if (String(p._id) === String(normalized._id)) {
          found = true;

          return {
            ...p,
            ...normalized,
            user: normalized.user || p.user,
          };
        }
        return p;
      });

      return found ? next : prev;
    });
  };

  // Listen for global post updates
  useEffect(() => {
    const onUpdated = (e) => {
      const post = e?.detail?.post;
      if (post?._id) updatePostInState(post);
    };

    window.addEventListener('post:updated', onUpdated);
    return () => window.removeEventListener('post:updated', onUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep authoritative view counts synchronized across the Home feed
  useEffect(() => {
    const handleViewCountUpdate = (event) => {
      const postId = String(event?.detail?.postId || '');
      const nextViewCount = Number(event?.detail?.viewCount);

      if (!postId || !Number.isFinite(nextViewCount)) return;

      const safeViewCount = Math.max(0, nextViewCount);

      setPosts((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) {
          return prev;
        }

        let changed = false;

        const nextPosts = prev.map((post) => {
          if (!post || post.type === 'sponsored') {
            return post;
          }

          let nextPost = post;

          const currentPostId = String(post._id || post.id || '');

          if (
            currentPostId === postId &&
            Number(post.viewCount || 0) !== safeViewCount
          ) {
            nextPost = {
              ...nextPost,
              viewCount: safeViewCount,
            };

            changed = true;
          }

          const original = nextPost.repostOf;

          if (original && typeof original === 'object') {
            const originalId = String(
              original._id || original.id || ''
            );

            if (
              originalId === postId &&
              Number(original.viewCount || 0) !== safeViewCount
            ) {
              nextPost = {
                ...nextPost,
                repostOf: {
                  ...original,
                  viewCount: safeViewCount,
                },
              };

              changed = true;
            }
          }

          return nextPost;
        });

        return changed ? nextPosts : prev;
      });
    };

    window.addEventListener(
      POST_VIEW_COUNT_UPDATED_EVENT,
      handleViewCountUpdate
    );

    return () => {
      window.removeEventListener(
        POST_VIEW_COUNT_UPDATED_EVENT,
        handleViewCountUpdate
      );
    };
  }, []);


  const getPostInteractions = async (postId, force = false) => {
    if (!force && postInteractions[postId]) return postInteractions[postId];
    try {
      const res = await api.get(`/posts/interactions/${postId}`);
      const data = res.data || {};
      setPostInteractions((prev) => ({ ...prev, [postId]: data }));
      return data;
    } catch (err) {
      console.error(`❌ Failed to fetch interactions for ${postId}`, err);
      return null;
    }
  };

  // Build the correct endpoint for each tab/mode
  // NOTE: seed + exclude are only needed for ranked-feed variants.
  const buildFeedUrl = (username, pageNum, limit, exclude, mode) => {
    const esc = (s) => (s ? `&exclude=${encodeURIComponent(s)}` : '');
    const seedParam = feedSeed ? `&seed=${encodeURIComponent(feedSeed)}` : '';
    const viewerParam = `&username=${encodeURIComponent(username)}`;

    switch (mode) {
      case 'following':
        // Following feed is unseen-first + paginated on backend
        return `/feed/following-feed/${username}?page=${pageNum}&limit=${limit}${viewerParam}`;

      case 'merchants':
        // Dedicated merchant feed; unseen-first + paginated
        return `/feed/merchant-feed/${username}?page=${pageNum}&limit=${limit}${viewerParam}`;

      case 'verified':
        // Dedicated verified-payments feed; unseen-first + paginated
        return `/feed/verified-feed/${username}?page=${pageNum}&limit=${limit}${viewerParam}`;

      case 'all':
      default:
        // Main ranked feed (uses seed + exclude)
        return `/feed/ranked-feed/${username}?page=${pageNum}&limit=${limit}${esc(
          exclude
        )}${seedParam}${viewerParam}`;
    }
  };

  // Load first page for a given mode (tab)
  const loadInitialPosts = async (username, force = false, mode = 'all') => {
    if (!username) {
      console.warn('⚠️ No username provided to fetch feed');
      setHasLoadedPosts(true);
      return;
    }

    if (hasLoadedPosts && !force && feedMode === mode) return;

    try {
      setLoading(true);
      setPage(1);
      setFeedMode(mode);

      // Pick up the seed the client may have placed into sessionStorage
      try {
        const sessSeed = sessionStorage.getItem('bf_feed_seed');
        setFeedSeed(sessSeed || null);
      } catch (err) {
        setFeedSeed(null);
      }

      const url = buildFeedUrl(username, 1, 10, '', mode);

      console.log('🟨 [PostContext] loadInitialPosts request', {
        username,
        mode,
        force,
        url,
        currentFeedSeed: feedSeed,
      });

      const res = await api.get(url);
      const data = res.data?.feed || [];

      console.log('🟨 [PostContext] loadInitialPosts response', {
        username,
        mode,
        postsCount: data.length,
        hasMore: !!res.data?.hasMore,
        firstPoll: data.find((p) => p?.poll)?.poll || null,
      });

      const enriched = enrichPosts(data);
      const hasMoreFromBackend = !!res.data?.hasMore;

      setPosts(enriched);
      setHasMore(hasMoreFromBackend);
      setHasLoadedPosts(true);
      setPage(2);
    } catch (err) {
      console.error('❌ Failed to load feed:', err);
      setPosts([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Load next page for current mode
  const fetchMorePosts = async (username) => {
    if (!username || loading || !hasMore) return;

    try {
      setLoading(true);

      const exclude = posts.map((p) => p._id).join(',');
      console.log(`🧹 Excluding ${posts.length} already-loaded post IDs from request`);

      const url = buildFeedUrl(username, page, 10, exclude, feedMode);

      console.log('🟨 [PostContext] fetchMorePosts request', {
        username,
        feedMode,
        page,
        excludeCount: posts.length,
        url,
      });

      const res = await api.get(url);
      const data = res.data?.feed || [];

      console.log('🟨 [PostContext] fetchMorePosts response', {
        username,
        feedMode,
        page,
        postsCount: data.length,
        hasMore: !!res.data?.hasMore,
        firstPoll: data.find((p) => p?.poll)?.poll || null,
      });

      const enriched = enrichPosts(data);
      const hasMoreFromBackend = !!res.data?.hasMore;

      setPosts((prev) => {
        const existingIds = new Set(prev.map((p) => p._id));
        const next = [...prev];

        enriched.forEach((post) => {
          if (!existingIds.has(post._id)) next.push(post);
        });

        return next;
      });

      setHasMore(hasMoreFromBackend);
      setPage((prev) => prev + 1);
    } catch (err) {
      console.error(`❌ Failed to fetch page ${page}`, err);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const clearPostState = () => {
    setPosts([]);
    setPostInteractions({});
    setPage(1);
    setHasMore(true);
    setHasLoadedPosts(false);
    setLoading(false);
    setFeedSeed(null);
  };

  return (
    <PostContext.Provider
      value={{
        posts,
        setPosts,
        hasLoadedPosts,
        loading,
        hasMore,
        page,
        feedMode,
        addPost,
        updatePostInState,
        getPostInteractions,
        loadInitialPosts,
        fetchMorePosts,
        clearPostState,
        feedSeed,
      }}
    >
      {children}
    </PostContext.Provider>
  );
}

export function usePostContext() {
  return useContext(PostContext);
}
